import Axios, { type InternalAxiosRequestConfig } from "axios";

import { env } from "@/config/env";
import { paths } from "@/config/paths";
import { toast } from "sonner";
import useCurrentUser, { type AuthState } from "@/store/useCurrentUser";
import { validateToken } from "./auth/token-validator";

// function authRequestInterceptor(config: InternalAxiosRequestConfig) {
//   if (config.headers) {
//     config.headers.Accept = "application/json";
//   }

//   config.withCredentials = true;
//   return config;
// }

export const api = Axios.create({
  baseURL: env.API_URL,
});

// interceptor to add the Authorization header with the token from the store
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    validateToken();

    const { token } = useCurrentUser.getState() as AuthState;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// interceptor to handle responses and errors especially if token has expired or is invalid
api.interceptors.response.use(
  async (response) => {
    // const {token} = useCurrentUser.getState();
    // if (response.config.headers && token) {
    //   response.config.headers = {
    //     ...response.config.headers,
    //     Authorization: `Bearer ${token}`,
    //   };
    // }
    return response.data;
  },
  (error) => {
    const message = error.response?.data?.message || error.message;
    const errorCode = error.response?.data?.code;
    const requestUrl = error.config?.url || "";
    const isLoginRequest = requestUrl.includes("/auth/login");

    if (error.response?.status === 401) {
      // Invalid credentials during login - show error but stay on page
      if (isLoginRequest || errorCode === "INVALID_CREDENTIALS") {
        toast.error(message || "Invalid login credentials.");
      }
      // Token expired or invalid - redirect to login
      else if (
        errorCode === "TOKEN_EXPIRED" ||
        errorCode === "TOKEN_INVALID" ||
        message?.includes("expired")
      ) {
        const { removeUser } = useCurrentUser.getState() as AuthState;
        removeUser();
        window.location.href = paths.auth.login.getHref(
          window.location.pathname
        );
        toast.error("Session expired. Please login again.");
      }
      // Fallback for other 401 errors
      else {
        const { removeUser } = useCurrentUser.getState() as AuthState;
        removeUser();
        window.location.href = paths.auth.login.getHref(
          window.location.pathname
        );
        toast.error(message || "Authentication failed. Please login again.");
      }
    } else {
      // Non-401 errors. Skip the toast for portal read (GET) failures — the
      // portal surfaces those errors inline with a reload action instead of
      // hammering the user with repeated toasts. Mutations still toast.
      const method = String(error.config?.method || "get").toLowerCase();
      const isPortalRead =
        requestUrl.includes("/api/portal/") && method === "get";
      if (message && !isPortalRead) {
        console.error("error:::", error);
        toast.error(message);
      }
    }

    return Promise.reject(error);
  }
);
